import {yupResolver} from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import {useEffect, useState, useCallback} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';
import {
  Container,
  Form,
  Grid,
  Header,
  Popup,
  Radio,
  Icon,
  Menu,
  Message,
  Button,
  Loader,
} from 'semantic-ui-react';
import * as yup from 'yup';

import {getChangedFieldsValues} from 'forms';
import {useReservedHostnames, useHttpClient, useSupportedWebeosServerVersions} from 'hooks';
import httpClient from 'httpClient';
import {addSuccessMessage, clearSuccessMessages} from 'state/actions';
import {getWebeosSiteLogsLink} from 'state/selectors';

import Matomo from '../../Common/Matomo';
import {
  validationSchemas,
  siteMinLength,
  sitesTypeInfo,
  buildSubdomainDropdownOptions,
  validateEosPath,
  siteSuccessMessage,
  formatWebEOSServerVersionsOptions,
} from '../../Common/sharedElements';
import QrCodeModal from '../../QrCode/QrCodeModal';

import styles from './UPDComponent.module.scss';

const allowedWebCrawlersOptions = [
  {
    text: 'None',
    value: 'NoIndexing',
  },
  {
    text: 'All',
    value: 'IndexByAllCrawlers',
  },
];

const defaultFormValues = {
  name: '',
  eosPath: '',
  subdomain: '.web.cern.ch',
  serverVersion: '',
  globalSettings: {
    allowAnonymousAccessByDefault: false,
    archiveSite: false,
    useDistributedConfigurationFiles: false,
  },
  allowedWebCrawlersIfNoRobotsTxt: 'NoIndexing',
  deletionDate: null,
};

const formSchema = yup.object().shape({
  name: validationSchemas.updateNameValidation
    .test('domainIsUnique', 'Such domain already exists', (value, ctx) => {
      if (value === ctx.parent.originalSiteName) {
        return true;
      }

      const siteDomain = value + ctx.parent.subdomain;
      if (ctx.options.context.reservedHostnames.includes(value)) {
        return ctx.createError({message: 'Provided site name is a reserved name'});
      }

      return !ctx.options.context.reservedFullyQualifiedDomainNames.includes(siteDomain);
    })
    .test(
      'minLength',
      `Minimum allowed length of a site name is ${siteMinLength} characters`,
      (value, ctx) => {
        const originalSiteName = ctx.parent.originalSiteName;
        if (originalSiteName !== undefined) {
          if (originalSiteName === value) {
            return true;
          } else {
            return value.length >= siteMinLength;
          }
        }
      }
    ),
  eosPath: validationSchemas.eosPathValidation.test('eosPathCategory', (value, ctx) => {
    try {
      return validateEosPath(value, ctx.options.context.category, ctx.options.context.ownerInfo);
    } catch (ex) {
      return ctx.createError({message: ex.message});
    }
  }),
  subdomain: yup.string().when('$isDisabled', {
    is: true,
    then: schema => schema.strip(),
    otherwise: validationSchemas.subdomainValidation.test(
      'unchangeableSubdomain',
      'This is a special site, please, open a SNoW ticket to request a subdomain change',
      (value, ctx) => {
        const originalSiteUrl = ctx.parent.siteUrl;
        const newSiteName = ctx.parent.name;

        // Only invalid if both the "root" changed AND name too short
        if (originalSiteUrl) {
          const oldSub = originalSiteUrl.slice(originalSiteUrl.indexOf('.'));
          const rootChanged = oldSub !== value;
          return !(rootChanged && newSiteName.length < siteMinLength);
        }
        return true;
      }
    ),
  }),
});

const UPDComponent = ({projectName, category, ownerInfo, onWebEOSSites}) => {
  const [upds, setUpds] = useState([]);
  const [activeItem, setActiveItem] = useState(null);
  const dispatch = useDispatch();
  const siteLogsLink = useSelector(getWebeosSiteLogsLink);
  const {
    reservedHostnames,
    reservedFullyQualifiedDomainNames,
    isLoading: isLoadingReservedHostnames,
  } = useReservedHostnames('webeos');

  // Allowed subdomain options
  const subdomainOptions = buildSubdomainDropdownOptions().map(({value}) => value);

  // Subdomain from the current UPD
  const siteUrl = upds?.[activeItem?.index]?.siteUrl;
  const siteSubdomain = upds?.[activeItem?.index]?.subdomain;

  // Handling the case when there are unique subdomains for the site
  const isCustomUrl = siteUrl && !subdomainOptions.some(option => siteUrl.endsWith(option));
  const isDisabled = isCustomUrl && !subdomainOptions.includes(siteSubdomain);

  const dropdownOptions = isCustomUrl
    ? [
        ...buildSubdomainDropdownOptions(),
        {text: siteSubdomain, key: siteSubdomain, value: siteSubdomain},
      ]
    : buildSubdomainDropdownOptions();

  // QR code
  const [qrUrl, setQrUrl] = useState('');

  const {
    control,
    handleSubmit,
    reset,
    formState: {isDirty, isSubmitting, isValid, dirtyFields, isSubmitSuccessful},
    trigger,
    getValues,
  } = useForm({
    context: {
      reservedHostnames,
      reservedFullyQualifiedDomainNames,
      category,
      ownerInfo,
      isCustomUrl,
      isDisabled,
    },
    mode: 'onChange',
    defaultValues: defaultFormValues,
    resolver: yupResolver(formSchema),
  });

  const {data, error, loading: isLoadingUpdDetails, cancel, refetch} = useHttpClient({
    url: `/api/webeos/${projectName}/upds`,
    method: 'GET',
  });

  const {
    supportedWebEOSVersions: webEOSVersions,
    isLoading: isLoadingSupportedWebEOSServerVersions,
    error: supportedWebEOSVersionError,
  } = useSupportedWebeosServerVersions();

  useEffect(() => {
    if (data && !isLoadingUpdDetails) {
      const upds = Object.entries(data.upds).map(([updName, updData]) => ({
        ...updData,
        updName,
        originalSiteName: updData.name,
      }));

      if (upds.length === 0) {
        return;
      }

      onWebEOSSites(Object.keys(data.upds));

      if (activeItem) {
        reset(upds[activeItem.index]);
      } else {
        reset(upds[0]);
        setActiveItem({
          index: 0,
          upd: upds[0],
        });
      }

      setUpds(upds);
    }
  }, [data, isLoadingUpdDetails, reset, projectName, setUpds, activeItem, onWebEOSSites]);

  useEffect(() => {
    trigger('eosPath');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, ownerInfo]);

  // reset form's values after a successful form submission
  useEffect(() => {
    if (isSubmitSuccessful) {
      refetch();
    }
  }, [isSubmitSuccessful, refetch]);

  useEffect(() => {
    return () => {
      cancel('Request cancelled since UPDComponent has been unmounted.');
    };
  }, [cancel]);

  const selectUpd = useCallback(
    (upd, index) => {
      reset(upd);
      setActiveItem({
        index: index,
        upd: upd,
      });
    },
    [reset]
  );

  const submitHandler = async data => {
    dispatch(clearSuccessMessages());

    let changedFields = getChangedFieldsValues(dirtyFields, data);

    // we need to include both name and subdomain (even if one of them changes) to make
    // sure that the backend can build the siteURL
    if (dirtyFields.name || dirtyFields.subdomain) {
      changedFields = {...changedFields, name: data.name, subdomain: data.subdomain};
    }

    await httpClient.patch(`/api/webeos/${projectName}/${data.updName}`, changedFields);
    dispatch(
      addSuccessMessage(siteSuccessMessage(projectName, dirtyFields.name || dirtyFields.subdomain))
    );
  };

  if (error) {
    return (
      <Message error>There was a problem while fetching the details of the WebEOS site.</Message>
    );
  }

  return !isLoadingUpdDetails && upds?.length === 0 ? (
    <Message>
      <Message.Header>This WebEOS site is misconfigured.</Message.Header>
      <p>
        There is no URL and no EOS path associated with this site. We recommend creating a new
        WebEOS site via the standard form.
      </p>
    </Message>
  ) : (
    <>
      <Container className={styles.UPDComponent}>
        <Grid columns={2} stackable>
          <Grid.Column width={5}>
            {isLoadingUpdDetails ? (
              <Loader inline="centered" active />
            ) : (
              <Menu pointing vertical>
                {upds?.map((upd, index) => {
                  return (
                    <Menu.Item
                      key={upd.name}
                      content={upd.siteUrl}
                      onClick={() => {
                        selectUpd(upd, index);
                      }}
                      active={activeItem.upd.updName === upd.updName}
                    />
                  );
                })}
              </Menu>
            )}
          </Grid.Column>
          <Grid.Column width={11}>
            <Form
              id="update-upd-form"
              onSubmit={handleSubmit(submitHandler)}
              loading={isLoadingUpdDetails || isSubmitting}
            >
              {getValues().deletionDate && (
                <Message icon warning visible>
                  <Icon name="attention" size="mini" />
                  <Message.Content>
                    <Message.Header>
                      This is a temporary site that will be deleted on{' '}
                      {new Date(getValues().deletionDate).toLocaleDateString()}.
                    </Message.Header>
                    You can find additional information in the{' '}
                    <a href="https://cern.service-now.com/service-portal?id=outage&n=OTG0096230">
                      OTG0096230
                    </a>
                    .
                  </Message.Content>
                </Message>
              )}

              <Grid columns={2} stackable>
                <Grid.Row>
                  <Grid.Column width={12}>
                    <Header as="h4" textAlign="center" dividing>
                      Served From
                    </Header>
                    <Grid.Row>
                      <Grid columns={3}>
                        <Grid.Column textAlign="right" width={3}>
                          <div className={styles.FixedLabel}>
                            <Popup
                              content="Choose descriptive and short name, following naming conventions."
                              position="left center"
                              trigger={<label>Site URL</label>}
                            />
                          </div>
                        </Grid.Column>
                        <Grid.Column width={6}>
                          <Controller
                            control={control}
                            name="name"
                            render={({field: {onChange, value, name}, fieldState: {error}}) => (
                              <Form.Input
                                name={name}
                                value={value}
                                error={error?.message}
                                onChange={(_evt, data) => onChange(data.value)}
                                aria-label="Site name"
                              />
                            )}
                          />
                        </Grid.Column>
                        <Grid.Column width={5} className={styles.Subdomain}>
                          <Controller
                            control={control}
                            name="subdomain"
                            render={({field: {onChange, value, name}, fieldState: {error}}) => (
                              <Form.Select
                                name={name}
                                value={value}
                                fluid
                                error={error?.message}
                                options={dropdownOptions}
                                disabled={isDisabled}
                                onChange={(_evt, data) => onChange(data.value)}
                                aria-label="Subdomain"
                              />
                            )}
                          />
                        </Grid.Column>

                        {isCustomUrl && isDisabled ? (
                          <Grid.Column width={1}>
                            <Popup
                              position="right center"
                              hoverable
                              content={
                                <div>
                                  This is a custom URL. If you want to edit it please, open a{' '}
                                  <a
                                    href="https://cern.service-now.com/service-portal?id=sc_cat_item&name=request&se=eos-web-hosting"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    SNOW Request
                                  </a>
                                  .
                                </div>
                              }
                              trigger={<Icon name="info" style={{marginTop: '0.75rem'}} />}
                            />
                          </Grid.Column>
                        ) : null}
                        <Grid.Column width={1}>
                          {!isDirty && (
                            <Popup
                              trigger={
                                <Icon
                                  className={styles.QrCodeIcon}
                                  name="qrcode"
                                  onClick={() => {
                                    const values = getValues();
                                    const currentUrl = `https://${values.name}${values.subdomain}`;
                                    setQrUrl(currentUrl);
                                  }}
                                />
                              }
                              position="top center"
                            >
                              Generate QR code
                            </Popup>
                          )}
                        </Grid.Column>
                      </Grid>
                    </Grid.Row>

                    <Grid.Row>
                      <Grid columns={3}>
                        <Grid.Column textAlign="right" widescreen={3}>
                          <div className={styles.FixedLabel}>
                            <Popup
                              content="Path to site content on EOS folder, e.g. /eos/user/j/john/www"
                              position="left center"
                              trigger={<label>EOS Path</label>}
                            />
                          </div>
                        </Grid.Column>
                        <Grid.Column textAlign="left" widescreen={13}>
                          <Controller
                            control={control}
                            name="eosPath"
                            render={({field: {onChange, value, name}, fieldState: {error}}) => (
                              <Form.Input
                                name={name}
                                value={value}
                                error={error?.message}
                                onChange={(_evt, data) => onChange(data.value)}
                                aria-label="EOS Path"
                              />
                            )}
                          />
                        </Grid.Column>
                      </Grid>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid columns={3}>
                        <Grid.Column textAlign="right" widescreen={3}>
                          <div className={styles.FixedLabel}>
                            <Popup
                              content="The version of the webEOS server"
                              position="left center"
                              trigger={<label>Version</label>}
                            />
                          </div>
                        </Grid.Column>
                        <Grid.Column width={7}>
                          <Controller
                            control={control}
                            name="serverVersion"
                            render={({field: {onChange, value, name}, fieldState: {error}}) => (
                              <Form.Select
                                name={name}
                                value={value}
                                error={
                                  error?.message ||
                                  (supportedWebEOSVersionError
                                    ? 'There was a problem while fetching supported WebEOS versions. Please wait a couple of minutes and refresh the page.'
                                    : undefined)
                                }
                                options={formatWebEOSServerVersionsOptions(webEOSVersions)}
                                onChange={(_evt, data) => onChange(data.value)}
                                aria-label="Version"
                                loading={isLoadingSupportedWebEOSServerVersions}
                              />
                            )}
                          />
                        </Grid.Column>
                        <Grid.Column verticalAlign="middle">
                          <a href="https://webeos.docs.cern.ch" target="_blank" rel="noreferrer">
                            Learn more
                          </a>
                        </Grid.Column>
                      </Grid>
                    </Grid.Row>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row columns={2}>
                  <Grid.Column className={styles.LeftColumn}>
                    <Header as="h4" dividing textAlign="center">
                      Site Access
                    </Header>
                    <Grid.Row>
                      <Grid columns={2}>
                        <Grid.Column textAlign="right" width={4}>
                          <Popup
                            content="Enable this if you want to allow guest access"
                            position="left center"
                            trigger={<label>Guest Access</label>}
                          />
                        </Grid.Column>
                        <Grid.Column verticalAlign="middle" textAlign="left">
                          <Controller
                            control={control}
                            name="globalSettings.allowAnonymousAccessByDefault"
                            render={({field: {onChange, value, name}}) => (
                              <Radio
                                toggle
                                name={name}
                                checked={value}
                                onChange={(_evt, data) => onChange(data.checked)}
                                aria-label="Guest Access"
                              />
                            )}
                          />
                        </Grid.Column>
                      </Grid>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid columns={2}>
                        <Grid.Column width={4}></Grid.Column>
                        <Grid.Column width={12}>
                          {getValues().globalSettings?.allowAnonymousAccessByDefault ? (
                            <p className={styles.GuestAccessMessage}>
                              Site is accessible to unauthenticated users; you may restrict access
                              to some parts of the site by adding rules in corresponding .htaccess
                              files
                            </p>
                          ) : (
                            <p className={styles.GuestAccessMessage}>
                              Site is accessible only to user groups defined explicitly in the
                              <a
                                href="https://application-portal.web.cern.ch/"
                                target="_blank"
                                rel="noreferrer"
                              >
                                {' '}
                                Application Portal.{' '}
                              </a>
                              These are set by default to all CERN account holders and EduGAIN
                              users, but can be easily changed.
                            </p>
                          )}
                          <a
                            className={styles.GuestAccessMessage}
                            href={`https://webeos.docs.cern.ch/authorization/${
                              getValues().globalSettings?.allowAnonymousAccessByDefault
                                ? ''
                                : '#providing-access-to-a-group-application-role'
                            }`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Learn More
                          </a>
                        </Grid.Column>
                      </Grid>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid columns={2}>
                        <Grid.Column textAlign="right" width={4}>
                          <Popup
                            content="Enable this option if you need specific behaviours or access restriction in some folders of the site"
                            position="left center"
                            trigger={<label>Use .htaccess files</label>}
                          />
                        </Grid.Column>
                        <Grid.Column textAlign="left" width={12} verticalAlign="middle">
                          <Controller
                            control={control}
                            name="globalSettings.useDistributedConfigurationFiles"
                            render={({field: {onChange, value, name}}) => (
                              <Radio
                                toggle
                                name={name}
                                checked={value}
                                onChange={(_evt, data) => onChange(data.checked)}
                                aria-label="Use .htaccess files"
                              />
                            )}
                          />
                        </Grid.Column>
                      </Grid>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid columns={2}>
                        <Grid.Column textAlign="right" verticalAlign="middle" width={4}>
                          <Popup
                            content="Select which crawlers you want to allow on your site"
                            position="left center"
                            trigger={<label>Allowed web crawlers</label>}
                          />
                        </Grid.Column>

                        <Grid.Column textAlign="left" verticalAlign="middle" width={12}>
                          <Controller
                            control={control}
                            name="allowedWebCrawlersIfNoRobotsTxt"
                            render={({field: {onChange, value, name}, fieldState: {error}}) => (
                              <Form.Select
                                name={name}
                                value={value === 'IndexByCernCrawler' ? 'NoIndexing' : value}
                                error={
                                  error && {
                                    content: error?.message,
                                    pointing: 'below',
                                  }
                                }
                                options={allowedWebCrawlersOptions}
                                onChange={(_evt, data) => onChange(data.value)}
                              />
                            )}
                          />
                        </Grid.Column>
                      </Grid>
                    </Grid.Row>
                  </Grid.Column>

                  <Grid.Column className={styles.RightColumn}>
                    <Header as="h4" dividing textAlign="center">
                      Advanced
                    </Header>
                    <Grid columns={2}>
                      <Grid.Column textAlign="right" verticalAlign="middle">
                        <Popup
                          content="Link to the docs of directory browsing"
                          position="left center"
                          trigger={<label>Directory Browsing</label>}
                        />
                      </Grid.Column>
                      <Grid.Column textAlign="left" verticalAlign="middle">
                        <a
                          href="https://webeos.docs.cern.ch/manage_site/#enabling-directory-browsing"
                          target="_blank"
                          rel="noreferrer"
                        >
                          See how to configure
                        </a>
                      </Grid.Column>
                    </Grid>
                    <Grid columns={2}>
                      <Grid.Column textAlign="right" verticalAlign="middle">
                        <Popup
                          content="Link to the docs of CGI Execution"
                          position="left center"
                          trigger={<label>CGI Execution</label>}
                        />
                      </Grid.Column>
                      <Grid.Column textAlign="left" verticalAlign="middle">
                        <a
                          href="https://webeos.docs.cern.ch/manage_site/#enabling-cgi-execution"
                          target="_blank"
                          rel="noreferrer"
                        >
                          See how to configure
                        </a>
                      </Grid.Column>
                    </Grid>
                    <Grid columns={2}>
                      <Grid.Column textAlign="right">
                        <Popup
                          content="Link to the docs of Advanced Access Control"
                          position="left center"
                          trigger={<label>Advanced Access Control</label>}
                        />
                      </Grid.Column>
                      <Grid.Column textAlign="left" verticalAlign="middle">
                        <a
                          href="https://webeos.docs.cern.ch/authorization/"
                          target="_blank"
                          rel="noreferrer"
                        >
                          See how to configure
                        </a>
                      </Grid.Column>
                    </Grid>
                    <Grid columns={2}>
                      <Grid.Column textAlign="right">
                        <Popup
                          content="Link to site logs"
                          position="left center"
                          trigger={<label>Troubleshooting</label>}
                        />
                      </Grid.Column>
                      <Grid.Column textAlign="left" verticalAlign="middle">
                        <a
                          href={`${siteLogsLink}/?project=${projectName}&userprovideddirectory=${
                            getValues().updName
                          }`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View Site Logs
                        </a>
                      </Grid.Column>
                    </Grid>
                    <Grid columns={2}>
                      <Grid.Column textAlign="right" verticalAlign="middle">
                        <Popup
                          content="Enable this option if the content of the site is not maintained anymore. The access to the site will be restricted to CERN users only, and an informational banner will be shown at the top. The content of the website will be preserved."
                          position="left center"
                          trigger={<label>Archive Site</label>}
                        />
                      </Grid.Column>
                      <Grid.Column verticalAlign="middle" textAlign="left">
                        <Controller
                          control={control}
                          name="globalSettings.archiveSite"
                          render={({field: {onChange, value, name}}) => (
                            <Radio
                              toggle
                              name={name}
                              checked={value}
                              onChange={(_evt, data) => onChange(data.checked)}
                              aria-label="Archive site"
                            />
                          )}
                        />
                      </Grid.Column>
                    </Grid>
                    <Grid>
                      <Grid.Row centered>
                        <Header as="h5">
                          <a href="https://webeos.docs.cern.ch">
                            <Icon name="share square" />
                            <Header.Content>{sitesTypeInfo['webeos']['info']}</Header.Content>
                          </a>
                        </Header>
                      </Grid.Row>
                    </Grid>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row columns={1}>
                  <Grid.Column textAlign="right">
                    <Button
                      type="submit"
                      className={styles.SaveButton}
                      disabled={
                        !isDirty ||
                        isLoadingReservedHostnames ||
                        !isValid ||
                        isLoadingSupportedWebEOSServerVersions
                      }
                    >
                      Save
                    </Button>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
            {activeItem !== null && (
              <Matomo
                key={activeItem.upd.updName}
                projectType="webeos"
                projectName={projectName}
                siteUrl={getValues().siteUrl}
                siteId={getValues().matomoSiteId}
                siteResourceName={getValues().updName}
              />
            )}
          </Grid.Column>
        </Grid>
      </Container>

      {/* QR Code Modal */}
      {qrUrl && (
        <QrCodeModal
          siteUrl={qrUrl}
          onClose={() => {
            setQrUrl('');
          }}
        />
      )}
    </>
  );
};

UPDComponent.propTypes = {
  projectName: PropTypes.string.isRequired,
  category: PropTypes.string,
  ownerInfo: PropTypes.object,
  onWebEOSSites: PropTypes.func.isRequired,
};

export default UPDComponent;
